import { TMDB_API_KEY, TMDB_BASE_URL, YOUTUBE_BASE_URL } from '../config';

// Helper function to retry API calls
const fetchWithRetry = async (url, options = {}, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response;
    } catch (error) {
      console.log(`Attempt ${attempt + 1}/${maxRetries} failed for ${url}: ${error.message}`);
      lastError = error;
      
      if (attempt < maxRetries - 1) {
        // Wait before retrying, with exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
      }
    }
  }
  
  throw lastError;
};

// Get current year and calculate decade ranges dynamically
const currentYear = new Date().getFullYear();
const currentYearMinus1 = currentYear - 1;
const currentYearMinus2 = currentYear - 2;
const lastDecadeStart = Math.floor(currentYearMinus2 / 10) * 10;
const lastDecadeMinus10Start = lastDecadeStart - 10;
const lastDecadeMinus20Start = lastDecadeStart - 20;

export const movieCategories = {
  years: [
    { label: 'All Time', value: '' },
    { label: currentYear.toString(), value: 'currentYear' },
    { label: currentYearMinus1.toString(), value: 'currentYearMinus1' },
    { label: currentYearMinus2.toString(), value: 'currentYearMinus2' },
    { label: `${lastDecadeStart}s`, value: 'lastDecade' },
    { label: `${lastDecadeMinus10Start}s`, value: 'lastDecadeMinus10' },
    { label: `${lastDecadeMinus20Start}s`, value: 'lastDecadeMinus20' },
    { label: 'Older Movies', value: 'older' }
  ],
  genres: [
    { label: 'All Genres', value: '' },
    { label: 'Action', value: 'action' },
    { label: 'Drama', value: 'drama' },
    { label: 'Comedy', value: 'comedy' },
    { label: 'Sci-Fi', value: 'scifi' }
  ]
};

// Genre ID mapping for TMDB API
const genreMapping = {
  'action': '28',
  'drama': '18',
  'comedy': '35',
  'scifi': '878'
};

// Reverse mapping for display purposes
const genreIdToName = {
  '28': 'Action',
  '12': 'Adventure',
  '16': 'Animation',
  '35': 'Comedy',
  '80': 'Crime',
  '99': 'Documentary',
  '18': 'Drama',
  '10751': 'Family',
  '14': 'Fantasy',
  '36': 'History',
  '27': 'Horror',
  '10402': 'Music',
  '9648': 'Mystery',
  '10749': 'Romance',
  '878': 'Science Fiction',
  '10770': 'TV Movie',
  '53': 'Thriller',
  '10752': 'War',
  '37': 'Western'
};

export const getGenreNameFromId = (genreId) => {
  return genreIdToName[genreId] || 'Unknown';
};

export const getPopularMovies = async (page = 1, filters = {}) => {
  let url = `${TMDB_BASE_URL}/discover/movie?api_key=${TMDB_API_KEY}&sort_by=vote_average.desc&vote_count.gte=1000&page=${page}`;

  if (filters.genre) {
    const genreId = genreMapping[filters.genre];
    if (genreId) {
      url += `&with_genres=${genreId}`;
    }
  }

  if (filters.year) {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentYearMinus1 = currentYear - 1;
    const currentYearMinus2 = currentYear - 2;
    const lastDecadeStart = Math.floor(currentYearMinus2 / 10) * 10;
    const lastDecadeMinus10Start = lastDecadeStart - 10;
    const lastDecadeMinus20Start = lastDecadeStart - 20;

    if (filters.year === 'currentYear') {
      url += `&primary_release_date.gte=${currentYear}-01-01&primary_release_date.lte=${currentYear}-12-31`;
    } else if (filters.year === 'currentYearMinus1') {
      url += `&primary_release_date.gte=${currentYearMinus1}-01-01&primary_release_date.lte=${currentYearMinus1}-12-31`;
    } else if (filters.year === 'currentYearMinus2') {
      url += `&primary_release_date.gte=${currentYearMinus2}-01-01&primary_release_date.lte=${currentYearMinus2}-12-31`;
    } else if (filters.year === 'lastDecade') {
      url += `&primary_release_date.gte=${lastDecadeStart}-01-01&primary_release_date.lte=${lastDecadeStart + 9}-12-31`;
    } else if (filters.year === 'lastDecadeMinus10') {
      url += `&primary_release_date.gte=${lastDecadeMinus10Start}-01-01&primary_release_date.lte=${lastDecadeMinus10Start + 9}-12-31`;
    } else if (filters.year === 'lastDecadeMinus20') {
      url += `&primary_release_date.gte=${lastDecadeMinus20Start}-01-01&primary_release_date.lte=${lastDecadeMinus20Start + 9}-12-31`;
    } else if (filters.year === 'older') {
      url += `&primary_release_date.lte=${lastDecadeMinus20Start - 1}-12-31`;
    }
  }

  try {
    const response = await fetchWithRetry(url);
    const data = await response.json();
    
    // Fetch director information for each movie with retry mechanism
    const moviesWithDirectors = await Promise.all(
      data.results.map(async (movie) => {
        try {
          const creditsUrl = `${TMDB_BASE_URL}/movie/${movie.id}/credits?api_key=${TMDB_API_KEY}`;
          const creditsResponse = await fetchWithRetry(creditsUrl, {}, 2, 500);
          const creditsData = await creditsResponse.json();
          
          // Find the director
          const director = creditsData.crew.find(crewMember => crewMember.job === 'Director');
          
          return {
            ...movie,
            director: director ? director.name : 'Unknown'
          };
        } catch (error) {
          console.error(`Error fetching credits for movie ${movie.id} after retries:`, error);
          return {
            ...movie,
            director: 'Unknown'
          };
        }
      })
    );
    
    return {
      ...data,
      results: moviesWithDirectors
    };
  } catch (error) {
    console.error('Error fetching movies after all retries:', error);
    throw error;
  }
};

export const getMovieDetails = async (movieId) => {
  try {
    // Create promises for all three API calls with retry mechanism
    const moviePromise = fetchWithRetry(`${TMDB_BASE_URL}/movie/${movieId}?api_key=${TMDB_API_KEY}`, {}, 3);
    const videosPromise = fetchWithRetry(`${TMDB_BASE_URL}/movie/${movieId}/videos?api_key=${TMDB_API_KEY}`, {}, 2);
    const creditsPromise = fetchWithRetry(`${TMDB_BASE_URL}/movie/${movieId}/credits?api_key=${TMDB_API_KEY}`, {}, 2);
    
    // Wait for all promises to resolve
    const [movieResponse, videosResponse, creditsResponse] = await Promise.all([
      moviePromise,
      videosPromise,
      creditsPromise
    ]);

    const movieData = await movieResponse.json();
    const videosData = await videosResponse.json();
    const creditsData = await creditsResponse.json();

    // Find the first YouTube trailer
    const trailer = videosData.results.find(
      video => video.type === 'Trailer' && video.site === 'YouTube'
    );

    // Find the director(s)
    const directors = creditsData.crew.filter(
      crewMember => crewMember.job === 'Director'
    );

    return {
      ...movieData,
      trailerLink: trailer ? `${YOUTUBE_BASE_URL}${trailer.key}` : null,
      directors: directors || [],
      director: directors.length > 0 ? directors[0].name : 'Unknown'
    };
  } catch (error) {
    console.error(`Error fetching movie details for ${movieId} after all retries:`, error);
    throw error;
  }
}; 